import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UrgencyBadge, StatusBadge, typeIcons } from "@/components/Badges";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterSelect } from "@/components/FilterSelect";
import DocumentFormModal from "@/components/DocumentFormModal";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Download, Paperclip, FileUp, FileText, ChevronDown, MapPin, Package, X, Archive, RefreshCw, Eye } from "lucide-react";

const TYPE_ORDER = ["statutory", "license", "contract", "litigation", "resolutions", "other"];
const KNOWN = ["statutory", "license", "contract", "litigation", "resolutions"];
const normType = (tp) => (KNOWN.includes(tp) ? tp : "other");

export default function Documents() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isAdmin, canEditAny, canEditDoc } = useAuth();
  const qc = useQueryClient();

  const [filters, setFilters] = useState({ company_id: "all", type: "all", status: "all", urgency: "all", search: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [formDoc, setFormDoc] = useState(null);
  const [collapsed, setCollapsed] = useState(() => Object.fromEntries(TYPE_ORDER.map((tp) => [tp, true])));
  const toggleSection = (tp) => setCollapsed((s) => ({ ...s, [tp]: !s[tp] }));
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  // Archive / renewal dialog state
  const [archiveDoc, setArchiveDoc] = useState(null);
  const [archOption, setArchOption] = useState("renewed");
  const [archReplacement, setArchReplacement] = useState(null);
  const [archNewFile, setArchNewFile] = useState(null);
  const [archQuery, setArchQuery] = useState("");
  const [archBusy, setArchBusy] = useState(false);
  const emptyMeta = { issue_date: "", expiry_date: "", responsible_person: "", counterpart: "", physical_location: "" };
  const [archMeta, setArchMeta] = useState(emptyMeta);
  const openArchive = (d) => {
    setArchiveDoc(d); setArchOption("renewed"); setArchReplacement(null); setArchNewFile(null); setArchQuery("");
    setArchMeta({ issue_date: "", expiry_date: "", responsible_person: d.responsible_person || "", counterpart: d.counterpart || "", physical_location: d.physical_location || "" });
  };

  const archiveUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setArchBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArchNewFile(data); setArchReplacement(null);
      toast.success(f.name);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setArchBusy(false); }
  };
  const confirmArchive = async () => {
    if (!archiveDoc) return;
    const body = { outcome: archOption };
    if (archOption === "renewed") {
      if (archNewFile) {
        body.new_file = archNewFile;
        body.new_issue_date = archMeta.issue_date || null;
        body.new_expiry_date = archMeta.expiry_date || null;
        body.new_responsible_person = archMeta.responsible_person || "";
        body.new_counterpart = archMeta.counterpart || "";
        body.new_physical_location = archMeta.physical_location || "";
      }
      else if (archReplacement) body.replacement_id = archReplacement.id;
      else { toast.error(t("renewalNeedsReplacement")); return; }
    }
    setArchBusy(true);
    try {
      await api.post(`/documents/${archiveDoc.id}/archive`, body);
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setArchiveDoc(null);
      toast.success(archOption === "renewed" ? t("renewed") + " ✓" : t("archivedStatus") + " ✓");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setArchBusy(false); }
  };

  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleMany = (items) => setSelected((prev) => { const n = new Set(prev); const all = items.every((d) => n.has(d.id)); items.forEach((d) => (all ? n.delete(d.id) : n.add(d.id))); return n; });
  const clearSelection = () => setSelected(new Set());
  const downloadSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setDownloading(true);
    try {
      const res = await api.post("/documents/bulk-download", { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "horizonte-documents.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("bulkReady"));
      clearSelection();
    } catch (e) {
      toast.error(t("bulkNone"));
    } finally {
      setDownloading(false);
    }
  };

  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    return p;
  }, [filters]);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["documents", params],
    queryFn: async () => (await api.get("/documents", { params })).data,
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/documents/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Deleted");
    },
  });

  const openNew = () => { setFormDoc(null); setModalOpen(true); };
  const openEdit = (d) => { setFormDoc(d); setModalOpen(true); };

  const TableHead = ({ items = [] }) => (
    <thead className="border-b bg-secondary/50">
      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
        <th className="w-10 px-4 py-3">
          <Checkbox checked={items.length > 0 && items.every((d) => selected.has(d.id))} onCheckedChange={() => toggleMany(items)} data-testid="select-all-section" aria-label="select all" />
        </th>
        <th className="px-4 py-3 font-semibold">{t("title")}</th>
        <th className="px-4 py-3 font-semibold">{t("company")}</th>
        <th className="px-4 py-3 font-semibold">{t("counterpart")}</th>
        <th className="px-4 py-3 font-semibold">{t("expiryDate")}</th>
        <th className="px-4 py-3 font-semibold">{t("urgency")}</th>
        <th className="px-4 py-3 font-semibold">{t("status")}</th>
        <th className="px-4 py-3 font-semibold">{t("responsible")}</th>
        <th className="px-4 py-3 font-semibold">{t("physicalLocation")}</th>
        <th className="px-4 py-3 text-right font-semibold">{t("actions")}</th>
      </tr>
    </thead>
  );

  const renderRow = (d) => (
    <tr key={d.id} data-testid={`document-row-${d.id}`} className="transition-colors hover:bg-secondary/40">
      <td className="px-4 py-3">
        <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggleOne(d.id)} data-testid={`select-doc-${d.id}`} aria-label="select document" />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 font-medium">
          {d.file && <Paperclip className="h-3.5 w-3.5 text-horizonte-orange" />}
          {d.title}
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{companyMap[d.company_id] || "-"}</td>
      <td className="px-4 py-3 text-muted-foreground">{d.counterpart || "—"}</td>
      <td className="px-4 py-3 font-mono text-xs">{d.expiry_date || "—"}</td>
      <td className="px-4 py-3"><UrgencyBadge urgency={d.urgency} testId={`urgency-${d.id}`} /></td>
      <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
      <td className="px-4 py-3 text-muted-foreground">{d.responsible_person || "—"}</td>
      <td className="px-4 py-3 text-muted-foreground">
        {d.physical_location ? (
          <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-horizonte-orange" />{d.physical_location}</span>
        ) : "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`view-${d.id}`} onClick={() => navigate(`/documents/${d.id}`)} title={t("viewLabel")}><Eye className="h-4 w-4" /></Button>
          {d.file && (
            <a href={fileUrl(d.file.storage_path)} target="_blank" rel="noreferrer" data-testid={`download-${d.id}`} title={t("download")}>
              <Button size="icon" variant="ghost" className="h-8 w-8"><Download className="h-4 w-4" /></Button>
            </a>
          )}
          {isAdmin && (
            <>
              <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-${d.id}`} onClick={() => openEdit(d)} title={t("editLabel")}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-${d.id}`} onClick={() => { if (window.confirm(t("confirmDelete"))) delMut.mutate(d.id); }} title={t("delete")}><Trash2 className="h-4 w-4" /></Button>
            </>
          )}
          {!isAdmin && canEditDoc(d.company_id, d.type) && (
            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-${d.id}`} onClick={() => openEdit(d)} title={t("editLabel")}><Pencil className="h-4 w-4" /></Button>
          )}
          {canEditDoc(d.company_id, d.type) && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-horizonte-orange hover:bg-horizonte-orange/10" data-testid={`archive-${d.id}`} onClick={() => openArchive(d)} title={t("confirmRenewalArchive")}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );

  const current = documents.filter((d) => !d.archived);
  const replacementCandidates = current.filter((d) => archiveDoc && d.id !== archiveDoc.id).filter((d) => {
    const q = archQuery.trim().toLowerCase();
    if (!q) return true;
    return (d.title || "").toLowerCase().includes(q) || (companyMap[d.company_id] || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("documents")}</h1>
          <p className="mt-1 text-muted-foreground">{t("documentsSub")}</p>
        </div>
        {canEditAny && (
          <Button onClick={openNew} data-testid="add-document-button" className="bg-renco-navy text-white hover:bg-renco-slate">
            <Plus className="mr-2 h-4 w-4" /> {t("addDocument")}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="search-input" className="pl-9" placeholder={t("search")} value={filters.search} onChange={(e) => setFilters((s) => ({ ...s, search: e.target.value }))} />
          </div>
          <FilterSelect testId="filter-company" value={filters.company_id} onChange={(v) => setFilters((s) => ({ ...s, company_id: v }))} placeholder={t("allCompanies")} options={[{ value: "all", label: t("allCompanies") }, ...companies.map((c) => ({ value: c.id, label: c.name }))]} />
          <FilterSelect testId="filter-type" value={filters.type} onChange={(v) => setFilters((s) => ({ ...s, type: v }))} placeholder={t("allTypes")} options={[{ value: "all", label: t("allTypes") }, { value: "statutory", label: t("statutory") }, { value: "license", label: t("license") }, { value: "contract", label: t("contract") }, { value: "litigation", label: t("litigation") }, { value: "resolutions", label: t("resolutions") }, { value: "other", label: t("other") }]} />
          <FilterSelect testId="filter-status" value={filters.status} onChange={(v) => setFilters((s) => ({ ...s, status: v }))} placeholder={t("allStatus")} options={[{ value: "all", label: t("allStatus") }, { value: "active", label: t("active") }, { value: "pending", label: t("pending") }, { value: "renewed", label: t("renewed") }]} />
          <FilterSelect testId="filter-urgency" value={filters.urgency} onChange={(v) => setFilters((s) => ({ ...s, urgency: v }))} placeholder={t("allUrgency")} options={[{ value: "all", label: t("allUrgency") }, { value: "expired", label: t("expired") }, { value: "critical", label: t("critical") }, { value: "warning", label: t("warning") }, { value: "safe", label: t("safe") }]} />
        </div>
      </Card>

      {/* Bulk selection bar */}
      {current.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-4 py-3" data-testid="bulk-bar">
          <Checkbox
            checked={selected.size > 0 && selected.size === current.length}
            onCheckedChange={() => toggleMany(current)}
            data-testid="select-all-documents"
            aria-label="select all documents"
          />
          <span className="text-sm text-muted-foreground" data-testid="bulk-selected-count">
            {selected.size > 0 ? `${selected.size} ${t("selected")}` : t("selectToDownload")}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selected.size > 0 && (
              <Button variant="ghost" size="sm" onClick={clearSelection} data-testid="bulk-clear-button" className="text-muted-foreground">
                <X className="mr-1 h-4 w-4" /> {t("clear")}
              </Button>
            )}
            <Button onClick={downloadSelected} disabled={!selected.size || downloading} data-testid="bulk-download-button" className="bg-horizonte-orange text-white hover:bg-renco-slate">
              <Package className="mr-2 h-4 w-4" />
              {downloading ? t("bulkPreparing") : `${t("bulkDownload")}${selected.size ? ` (${selected.size})` : ""}`}
            </Button>
          </div>
        </div>
      )}

      {/* Current documents — grouped by type */}
      <div>
        <h2 className="mb-3 font-display text-xl font-semibold">{t("currentDocuments")}</h2>
        {isLoading ? (
          <Card className="p-10 text-center text-muted-foreground">…</Card>
        ) : current.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground" data-testid="no-documents">{t("noDocuments")}</Card>
        ) : (
          <div className="space-y-6">
            {TYPE_ORDER.map((tp) => {
              const items = current.filter((d) => normType(d.type) === tp);
              if (items.length === 0) return null;
              const Icon = typeIcons[tp] || FileText;
              const isOpen = !collapsed[tp];
              return (
                <Card key={tp} className="overflow-hidden" data-testid={`type-section-${tp}`}>
                  <button
                    type="button"
                    onClick={() => toggleSection(tp)}
                    data-testid={`type-toggle-${tp}`}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-2 border-b bg-secondary/40 px-4 py-3 text-left transition-colors hover:bg-secondary"
                  >
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                    <Icon className="h-4 w-4 text-horizonte-orange" />
                    <h3 className="font-display text-sm font-semibold uppercase tracking-wide">{t(tp)}</h3>
                    <span className="rounded-full bg-horizonte-orange/15 px-2 py-0.5 text-xs font-bold text-horizonte-orange">{items.length}</span>
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`documents-table-${tp}`}>
                        <TableHead items={items} />
                        <tbody className="divide-y">{items.map(renderRow)}</tbody>
                      </table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Archive / renewal dialog */}
      <Dialog open={!!archiveDoc} onOpenChange={(o) => !o && setArchiveDoc(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="archive-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{t("renewArchiveTitle")}</DialogTitle>
            <DialogDescription>{archiveDoc?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => setArchOption("renewed")} data-testid="arch-option-renewed" className={`rounded-lg border p-3 text-left transition-colors ${archOption === "renewed" ? "border-horizonte-orange bg-horizonte-orange/5" : "hover:bg-secondary/40"}`}>
                <div className="flex items-center gap-2 font-medium"><RefreshCw className="h-4 w-4 text-horizonte-orange" /> {t("optRenewed")}</div>
                <div className="mt-1 text-xs text-muted-foreground">{t("optRenewedDesc")}</div>
              </button>
              <button type="button" onClick={() => setArchOption("archived")} data-testid="arch-option-archived" className={`rounded-lg border p-3 text-left transition-colors ${archOption === "archived" ? "border-horizonte-orange bg-horizonte-orange/5" : "hover:bg-secondary/40"}`}>
                <div className="flex items-center gap-2 font-medium"><Archive className="h-4 w-4 text-muted-foreground" /> {t("optNotRequired")}</div>
                <div className="mt-1 text-xs text-muted-foreground">{t("optNotRequiredDesc")}</div>
              </button>
            </div>

            {archOption === "renewed" && (
              <div className="space-y-3 rounded-lg border p-3">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("uploadNew")}</Label>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-sm transition-colors hover:border-horizonte-orange hover:bg-secondary/40" data-testid="arch-file-label">
                  {archBusy ? <span className="text-muted-foreground">…</span> : <FileUp className="h-4 w-4 text-horizonte-orange" />}
                  <span className="truncate">{archNewFile ? archNewFile.original_filename : t("uploadFile")}</span>
                  <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={archiveUpload} data-testid="arch-file-input" />
                </label>
                {archNewFile && (
                  <div className="space-y-3 rounded-lg border bg-secondary/20 p-3" data-testid="arch-new-doc-meta">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("newDocDetails")}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("issueDate")}</Label>
                        <Input type="date" data-testid="arch-issue-input" value={archMeta.issue_date} onChange={(e) => setArchMeta((s) => ({ ...s, issue_date: e.target.value }))} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("expiryDate")}</Label>
                        <Input type="date" data-testid="arch-expiry-input" value={archMeta.expiry_date} onChange={(e) => setArchMeta((s) => ({ ...s, expiry_date: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("responsible")}</Label>
                        <Input data-testid="arch-responsible-input" value={archMeta.responsible_person} onChange={(e) => setArchMeta((s) => ({ ...s, responsible_person: e.target.value }))} />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">{t("counterpart")}</Label>
                        <Input data-testid="arch-counterpart-input" value={archMeta.counterpart} onChange={(e) => setArchMeta((s) => ({ ...s, counterpart: e.target.value }))} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">{t("physicalLocation")}</Label>
                      <Input data-testid="arch-physical-input" value={archMeta.physical_location} onChange={(e) => setArchMeta((s) => ({ ...s, physical_location: e.target.value }))} placeholder={t("physicalLocationPlaceholder")} />
                    </div>
                  </div>
                )}
                <div className="text-center text-xs font-medium text-muted-foreground">{t("orSelectExisting")}</div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9" placeholder={t("searchReplacement")} value={archReplacement ? archReplacement.title : archQuery} onChange={(e) => { setArchReplacement(null); setArchQuery(e.target.value); }} data-testid="arch-replacement-search" />
                </div>
                {!archReplacement && archQuery.trim() && (
                  <div className="max-h-44 overflow-y-auto rounded-lg border" data-testid="arch-replacement-list">
                    {replacementCandidates.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">{t("noReplacementFound")}</div>}
                    {replacementCandidates.slice(0, 50).map((d) => (
                      <button type="button" key={d.id} data-testid={`arch-replacement-${d.id}`} onClick={() => { setArchReplacement({ id: d.id, title: d.title }); setArchNewFile(null); setArchQuery(""); }} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary/60">
                        <span className="truncate">{d.title}</span>
                        <span className="ml-2 shrink-0 text-xs text-muted-foreground">{companyMap[d.company_id]} · {t(normType(d.type))}</span>
                      </button>
                    ))}
                  </div>
                )}
                {archReplacement && (
                  <div className="flex items-center justify-between rounded-lg bg-horizonte-orange/10 px-3 py-2 text-sm" data-testid="arch-replacement-selected">
                    <span className="font-medium">{archReplacement.title}</span>
                    <button type="button" onClick={() => setArchReplacement(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDoc(null)} data-testid="arch-cancel-button">{t("cancel")}</Button>
            <Button onClick={confirmArchive} disabled={archBusy} data-testid="arch-confirm-button" className="bg-horizonte-orange text-white hover:bg-renco-slate">{archBusy ? t("saving") : t("confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentFormModal open={modalOpen} onOpenChange={setModalOpen} doc={formDoc} companies={companies} />
    </div>
  );
}
